import React, { useState } from 'react';
import Menu from '@mui/material/Menu';
import CircularProgress from '@mui/material/CircularProgress';
import {
  ToolbarButton,
  FilterPanelTrigger,
  ExportCsv,
  ExportPrint,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-pro';
import ColumnViewMenu from './ColumnViewMenu';

const EXPORT_LOADING_MS = 500;

function runWithExportLoading(setIsExporting, action) {
  setIsExporting(true);
  try {
    action();
  } finally {
    setTimeout(() => setIsExporting(false), EXPORT_LOADING_MS);
  }
}

const toolbarIconColor = (active) => (active ? 'primary' : 'default');

export const toolbarIconGroupSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  alignSelf: 'center',
};

export function GridToolbarColumnsButton({ columnLayout = null }) {
  const rootProps = useGridRootProps();
  if (rootProps.disableColumnSelector) return null;
  return <ColumnViewMenu columnLayout={columnLayout} />;
}

export function GridToolbarFilterButton() {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const Tooltip = rootProps.slots.baseTooltip;
  const Badge = rootProps.slots.baseBadge;
  const FilterIcon = rootProps.slots.openFilterButtonIcon;

  if (rootProps.disableColumnFilter) {
    return null;
  }

  return (
    <FilterPanelTrigger
      render={(triggerProps, state) => (
        <Tooltip
          title={apiRef.current.getLocaleText('toolbarFiltersLabel')}
          enterDelay={1000}
          {...rootProps.slotProps?.baseTooltip}
        >
          <ToolbarButton
            {...triggerProps}
            color={toolbarIconColor(state.open || state.filterCount > 0)}
            aria-label={apiRef.current.getLocaleText('toolbarFiltersLabel')}
          >
            <Badge
              badgeContent={state.filterCount}
              color="primary"
              {...rootProps.slotProps?.baseBadge}
            >
              <FilterIcon fontSize="small" />
            </Badge>
          </ToolbarButton>
        </Tooltip>
      )}
    />
  );
}

export function GridToolbarExportButton({ options }) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const Tooltip = rootProps.slots.baseTooltip;
  const ExportIcon = rootProps.slots.exportIcon;
  const [isExporting, setIsExporting] = useState(false);
  const label = isExporting ? 'Downloading' : apiRef.current.getLocaleText('toolbarExport');

  return (
    <ExportCsv
      options={options}
      render={(triggerProps) => (
        <Tooltip
          title={label}
          enterDelay={isExporting ? 0 : 1000}
          {...rootProps.slotProps?.baseTooltip}
        >
          <ToolbarButton
            {...triggerProps}
            aria-label={label}
            onClick={(event) => {
              if (isExporting) return;
              runWithExportLoading(setIsExporting, () => triggerProps.onClick?.(event));
            }}
          >
            {isExporting ? <CircularProgress size={20} sx={{ display: 'block' }} /> : <ExportIcon fontSize="small" />}
          </ToolbarButton>
        </Tooltip>
      )}
    />
  );
}

export function GridToolbarExportMenu({ csvOptions, printOptions }) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const Tooltip = rootProps.slots.baseTooltip;
  const ExportIcon = rootProps.slots.exportIcon;
  const MenuItem = rootProps.slots.baseMenuItem;
  const [anchorEl, setAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const open = Boolean(anchorEl);
  const showCsv = !csvOptions?.disableToolbarButton;
  const showPrint = !printOptions?.disableToolbarButton;
  const label = isExporting ? 'Downloading' : apiRef.current.getLocaleText('toolbarExport');

  if (!showCsv && !showPrint) {
    return null;
  }

  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      <Tooltip
        title={label}
        enterDelay={isExporting ? 0 : 1000}
        disableInteractive={open}
        {...rootProps.slotProps?.baseTooltip}
      >
        <ToolbarButton
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          color={toolbarIconColor(open)}
          aria-label={label}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          {isExporting ? <CircularProgress size={20} sx={{ display: 'block' }} /> : <ExportIcon fontSize="small" />}
        </ToolbarButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {showPrint && (
          <ExportPrint
            options={printOptions}
            render={(triggerProps) => (
              <MenuItem
                {...rootProps.slotProps?.baseMenuItem}
                onClick={(event) => {
                  closeMenu();
                  runWithExportLoading(setIsExporting, () => triggerProps.onClick?.(event));
                }}
              >
                {apiRef.current.getLocaleText('toolbarExportPrint')}
              </MenuItem>
            )}
          />
        )}
        {showCsv && (
          <ExportCsv
            options={csvOptions}
            render={(triggerProps) => (
              <MenuItem
                {...rootProps.slotProps?.baseMenuItem}
                onClick={(event) => {
                  closeMenu();
                  runWithExportLoading(setIsExporting, () => triggerProps.onClick?.(event));
                }}
              >
                {apiRef.current.getLocaleText('toolbarExportCSV')}
              </MenuItem>
            )}
          />
        )}
      </Menu>
    </>
  );
}
